import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaPaperPlane,
  FaTimes,
} from "react-icons/fa";
import Header from "../components/AdminHeader";

const PREVIEW_FROM_EMAIL = "benton-theatres@gmail.com";
const BANNER_URL =
  "https://benton.lightideaproductions.co.uk/online-bookings-banner.png";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildActualEmailCardHtml({ subject, content }) {
  const safeBannerUrl = escapeHtml(BANNER_URL);
  const safeSubject = escapeHtml(subject || "Email Subject");
  const safeContent = escapeHtml(content || "").replace(/\n/g, "<br />");

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
      <tr>
        <td align="center">
          <table
            role="presentation"
            width="100%"
            cellspacing="0"
            cellpadding="0"
            border="0"
            style="max-width:760px;background:#101a2b;border:1px solid #1d3354;border-radius:18px;overflow:hidden;"
          >
            <tr>
              <td style="padding:0;">
                <img
                  src="${safeBannerUrl}"
                  alt="Benton Theatres"
                  style="display:block;width:100%;height:auto;border:0;"
                />
              </td>
            </tr>

            <tr>
              <td style="padding:32px 32px 12px 32px;color:#ffffff;">
                <h1 style="margin:0;font-size:32px;line-height:1.2;font-weight:700;">
                  Benton Theatres
                </h1>
                <p style="margin:10px 0 0 0;font-size:16px;line-height:1.7;color:#c9d4e5;">
                  ${safeSubject}
                </p>
              </td>
            </tr>

            <tr>
              <td style="padding:8px 32px 0 32px;color:#ffffff;">
                <p style="margin:0 0 18px 0;font-size:16px;line-height:1.8;color:#ffffff;">
                  Dear Recipient,
                </p>

                <p style="margin:0 0 18px 0;font-size:15px;line-height:1.8;color:#c9d4e5;">
                  ${safeContent || "Your email content will appear here."}
                </p>
              </td>
            </tr>

            <tr>
              <td style="padding:4px 32px 32px 32px;color:#ffffff;">
                <p style="margin:0;font-size:15px;line-height:1.8;color:#d7deea;">
                  Warm Regards,<br />
                  Benton Support Team.
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;
}

function buildPreviewHtml({ recipients, subject, content }) {
  const safeFrom = escapeHtml(PREVIEW_FROM_EMAIL);
  const safeTo =
    recipients.length > 0
      ? recipients.map((email) => escapeHtml(email)).join(", ")
      : "No recipients added";
  const safeSubject = escapeHtml(subject || "No subject");
  const emailCardHtml = buildActualEmailCardHtml({ subject, content });

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${safeSubject}</title>
      </head>
      <body style="margin:0;padding:0;background:#f3f6fb;font-family:Arial,Helvetica,sans-serif;">
        <div style="padding:24px;">
          <div style="max-width:900px;margin:0 auto;">
            <div style="background:#ffffff;border:1px solid #d7dfeb;border-radius:16px;overflow:hidden;box-shadow:0 12px 30px rgba(0,0,0,0.08);margin-bottom:20px;">
              <div style="padding:20px 24px;background:#f8fbff;">
                <div style="font-size:14px;line-height:1.9;color:#1f2937;">
                  <div><strong>From:</strong> ${safeFrom}</div>
                  <div><strong>To:</strong> ${safeTo}</div>
                  <div><strong>Subject:</strong> ${safeSubject}</div>
                </div>
              </div>
            </div>

            ${emailCardHtml}
          </div>
        </div>
      </body>
    </html>
  `;
}

export default function Email() {
  const [recipientInput, setRecipientInput] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState("");

  const previewHtml = useMemo(
    () => buildPreviewHtml({ recipients, subject, content }),
    [recipients, subject, content],
  );

  const addRecipient = (rawValue) => {
    const value = rawValue.trim().replace(/,$/, "");

    if (!value) return;

    if (!isValidEmail(value)) {
      setPageError(`"${value}" is not a valid email address.`);
      return;
    }

    if (recipients.some((item) => item.toLowerCase() === value.toLowerCase())) {
      setRecipientInput("");
      return;
    }

    setRecipients((prev) => [...prev, value]);
    setRecipientInput("");
    setPageError("");
  };

  const removeRecipient = (email) => {
    setRecipients((prev) => prev.filter((item) => item !== email));
  };

  const handleRecipientKeyDown = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      addRecipient(recipientInput);
      return;
    }

    if (e.key === "Backspace" && !recipientInput && recipients.length > 0) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const handleRecipientChange = (e) => {
    const value = e.target.value;

    if (value.includes(",")) {
      const parts = value.split(",");
      const allButLast = parts.slice(0, -1);

      allButLast.forEach((part) => addRecipient(part));
      setRecipientInput(parts[parts.length - 1] || "");
      return;
    }

    setRecipientInput(value);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setPageError("");
    setToast("");

    if (!recipients.length) {
      setPageError("Please add at least one recipient.");
      return;
    }

    if (!subject.trim()) {
      setPageError("Please enter an email subject.");
      return;
    }

    if (!content.trim()) {
      setPageError("Please enter your email content.");
      return;
    }

    setSending(true);

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recipients,
          subject: subject.trim(),
          content: content.trim(),
        }),
      });

      const rawText = await response.text();

      let result = {};
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch {
        result = { error: rawText || "Failed to send email." };
      }

      if (!response.ok) {
        throw new Error(result?.error || "Failed to send email.");
      }

      setToast(
        `Email sent to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}.`,
      );
      setRecipients([]);
      setRecipientInput("");
      setSubject("");
      setContent("");
    } catch (error) {
      console.error("Failed to send email:", error);
      setPageError(error.message || "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#08111f] text-white">
        <div className="pointer-events-none fixed inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative mx-auto max-w-[1800px] px-4 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
          <section className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(135deg,rgba(86,24,113,0.45),rgba(39,78,125,0.45),rgba(18,111,135,0.40))] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl">
            <div className="grid gap-8 px-5 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-10">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
                  <FaEnvelope />
                  <span>Email Composer</span>
                </div>

                <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
                  Benton Email System
                </h1>

                <p className="mt-4 max-w-3xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
                  Add recipients, write your message, preview the email, and send it directly through the Benton mail system.
                </p>
              </div>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur-xl sm:p-5">
                <div className="mb-4 text-sm font-medium text-white/90">
                  Email summary
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Recipients
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {recipients.length}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Subject
                    </div>
                    <div className="mt-2 truncate text-sm font-semibold text-white">
                      {subject || "No subject"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Status
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">
                      {sending ? "Sending..." : "Ready"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="grid gap-8 xl:grid-cols-[1fr_1fr]">
            <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                  Compose
                </div>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                  New Email
                </h2>
              </div>

              {pageError ? (
                <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>{pageError}</span>
                </div>
              ) : null}

              {toast ? (
                <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                  <FaCheckCircle className="mt-0.5 shrink-0" />
                  <span>{toast}</span>
                </div>
              ) : null}

              <form onSubmit={handleSendEmail} className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Recipients
                  </label>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] px-3 py-3">
                    <div className="flex min-h-[52px] flex-wrap items-center gap-2">
                      {recipients.map((email) => (
                        <div
                          key={email}
                          className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1.5 text-sm text-cyan-100"
                        >
                          <span>{email}</span>
                          <button
                            type="button"
                            onClick={() => removeRecipient(email)}
                            className="text-cyan-100/80 transition hover:text-white"
                          >
                            <FaTimes className="h-3 w-3" />
                          </button>
                        </div>
                      ))}

                      <input
                        type="text"
                        value={recipientInput}
                        onChange={handleRecipientChange}
                        onKeyDown={handleRecipientKeyDown}
                        placeholder="Type email and press comma"
                        className="min-w-[220px] flex-1 bg-transparent px-2 py-2 text-sm text-white outline-none placeholder:text-white/35"
                      />
                    </div>
                  </div>

                  <p className="mt-2 text-xs text-white/50">
                    Type an email address and press comma or Enter to add it.
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter email subject"
                    className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Content
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your email content here"
                    rows={12}
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaPaperPlane />
                  <span>{sending ? "Sending..." : "Send Email"}</span>
                </button>
              </form>
            </section>

            <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                  Preview
                </div>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                  Email Preview
                </h2>
              </div>

              <div
                className="overflow-hidden rounded-[24px] bg-white"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </section>
          </div>
        </div>
      </div>
    </>
  );
}